import React, { CSSProperties, ReactNode } from "react";
import { useDebug } from "@me8eon/debug";
import { useFeatureFlag } from "@me8eon/featureflags";
import { dataAsJsonFF, DataHasType, useDataPlugins } from "@me8eon/data";
import { useThrowError } from "@me8eon/throw_error";
import { DataWidgetLayout, debugDataWidgets } from "@me8eon/data_widget";
import { ClickFeedbackPayload, LikeOrDislike, SearchFeedbackPayload, useDisplayFeedbackComponent, useFeedback } from "@me8eon/feedback";
import { UrlOnClickProvider } from "@me8eon/renderers";
import { useSearchTextFilter } from "@me8eon/all_filters";
import { useDevModeSelected } from "@me8eon/devmode";
import { Explain } from "@me8eon/diagnostics";
import { notFromindicesAndHighScore } from "@me8eon/data_widget";
import { EnvironmentConfig } from "@me8eon/config";
import { useUserData } from "@me8eon/react_authentication";

export type DisplayAsResultsListProps<Data> = {
    rootId: string
    data: Data[]
    widgetName: string //just for debugging
}
export type DisplayAsResultsList = <Data extends DataHasType> (props: DisplayAsResultsListProps<Data>) => ReactNode

const preStyleForDebug: CSSProperties = {
    maxWidth: "1500px",
    width: "100%",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    overflowX: "auto", // Optional: Adds horizontal scrollbar if needed
    backgroundColor: "#f5f5f5", // Optional: Improve readability
    padding: "10px", // Optional: Add some padding
    borderRadius: "4px", // Optional: Rounded corners
};

export const DisplayAsResultsList = <Data extends DataHasType>(filter: (d: DataHasType) => boolean,
    Layout: DataWidgetLayout) =>
    ({ rootId, data, widgetName }: DisplayAsResultsListProps<Data>): ReactNode => {
        const debug = useDebug(debugDataWidgets);
        const dataPlugins = useDataPlugins();
        const throwError = useThrowError();
        const ff = useFeatureFlag(dataAsJsonFF);
        const { featureFlagEnabled } = useUserData();

        const ChildComponent = ({ relevancyEnabled }: { relevancyEnabled: boolean }) => {

            const filterRestByScore = relevancyEnabled && widgetName === "restOfElasticSearch";
            const filteredData = data.filter(filterRestByScore ? notFromindicesAndHighScore([EnvironmentConfig.appIndex], featureFlagEnabled ? 10 : 10) : filter);

            const [searchTerm] = useSearchTextFilter();
            const feedback = useFeedback();
            const Feedback = useDisplayFeedbackComponent();
            const userData = useUserData();
            const explain = useDevModeSelected()[0] === "Explain";

            return <> {
                filteredData.map((d, i) => {
                    const type = d.type;
                    const plugin = dataPlugins[type];
                    if (!plugin) {
                        return throwError("s/w", `No plugin for data type ${type}. Legal values are ${Object.keys(dataPlugins).sort()}\n${JSON.stringify(d, null, 2)}`);
                    }
                    const { DisplayData, idFrom } = plugin;
                    const id = idFrom(d);
                    const title = (d as any).title || (d as any).card_title;
                    const resultUrl = (d as any).url
                        || (d as any).card_resource_url
                        || (d as any).link
                        || (d as any)?.resource?.webUrl
                        || (d as any)?.resource?.subject
                        || (d as any)?.resource?.displayName;


                    function dataToSend(likeDislike: LikeOrDislike | undefined) {
                        const payload: SearchFeedbackPayload = {
                            source: "search",
                            query: searchTerm,
                            answer: resultUrl,
                            like: likeDislike,
                            index: (d as any).index,
                            id: id,
                            company: userData.company || "unknown",
                            role: userData.currentRole || "unknown",
                        };
                        return payload;
                    }

                    function urlOnClick(url: string | undefined) {
                        return () => {
                            const payload: ClickFeedbackPayload = {
                                source: "click",
                                index: (d as any).index,
                                id: id,
                                href: url || "No url specified",
                                position: i,
                            };
                            feedback(payload);
                        };
                    }

                    debug("DisplayAsResultsList - item", title, { rootId, i, id, d, plugin });
                    return <UrlOnClickProvider key={id} urlOnClick={urlOnClick}>
                        <div>{ff ?
                            <pre style={preStyleForDebug}>{JSON.stringify(d, null, 2)}</pre> :
                            <DisplayData key={id} rootId={rootId} id={id} data={d} />}
                            <Feedback dataToSend={dataToSend} source="query" />
                            {explain && <Explain id={(d as any)._id} index={(d as any)._index} query={searchTerm} />}

                        </div>
                    </UrlOnClickProvider>;
                })
            }
            </>
        }
        return <Layout rootId={rootId}><ChildComponent relevancyEnabled={true} /></Layout>;
    };